import { useForm } from '@tanstack/react-form';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PendingQueueRow } from '../../api/pending';
import { ErrorMessage } from '../common/ErrorMessage';
import { Modal } from '../common/Modal';

/** Ties the label to the textarea. */
const REASON_FIELD_ID = 'review-reject-reason';

const MIN_ZOOM = 1;
const MAX_ZOOM = 6;
const ZOOM_STEP = 1.25;

export interface PhotoReviewDialogProps {
  /** Row under review, or null when the dialog is closed. */
  item: PendingQueueRow | null;
  /** 0-based rank of `item` in the queue, and the queue length, for "3 / 12". */
  index: number;
  total: number;
  /** False when the reader submitted this photo themselves (four-eyes rule). */
  canApprove: boolean;
  /** An approve/reject call is in flight — every control locks. */
  busy: boolean;
  /** Last failure, surfaced here because the list behind is not visible. */
  error: string | null;
  onApprove: () => void;
  onReject: (reason: string | undefined) => void;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
}

function clampZoom(z: number) {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
}

const zoomButtonStyle = {
  padding: '6px 12px',
  fontSize: '13px',
};

/** Typing in a field must not also walk the queue or zoom the photo. */
function isTypingTarget(target: EventTarget | null) {
  const el = target instanceof HTMLElement ? target : null;
  return (
    el?.tagName === 'TEXTAREA' ||
    el?.tagName === 'INPUT' ||
    el?.isContentEditable === true
  );
}

function useRejectReasonForm(
  defaultReason: string,
  onReject: (reason: string | undefined) => void,
) {
  return useForm({
    defaultValues: { reason: defaultReason },
    onSubmit: ({ value }) => {
      const trimmed = value.reason.trim();
      onReject(trimmed.length > 0 ? trimmed : undefined);
    },
  });
}

type RejectReasonForm = ReturnType<typeof useRejectReasonForm>;

/** Zoom level and pan offset of the photo, with the pointer handlers that pan it. */
function usePhotoView() {
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ x: number; y: number } | null>(null);

  const resetView = useCallback(() => {
    setZoom(MIN_ZOOM);
    setOffset({ x: 0, y: 0 });
  }, []);

  // Zooming back out to 1x re-centres, so panning can never strand the photo
  // outside its viewport.
  const applyZoom = useCallback((next: number) => {
    const z = clampZoom(next);
    setZoom(z);
    if (z === MIN_ZOOM) {
      setOffset({ x: 0, y: 0 });
    }
  }, []);

  const startDrag = (e: React.PointerEvent<HTMLImageElement>) => {
    if (zoom === MIN_ZOOM) {
      return;
    }
    dragRef.current = { x: e.clientX - offset.x, y: e.clientY - offset.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const moveDrag = (e: React.PointerEvent<HTMLImageElement>) => {
    const start = dragRef.current;
    if (!start) {
      return;
    }
    setOffset({ x: e.clientX - start.x, y: e.clientY - start.y });
  };

  const endDrag = (e: React.PointerEvent<HTMLImageElement>) => {
    if (!dragRef.current) {
      return;
    }
    dragRef.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  return {
    zoom,
    offset,
    resetView,
    applyZoom,
    startDrag,
    moveDrag,
    endDrag,
  };
}

type PhotoView = ReturnType<typeof usePhotoView>;

/**
 * Escape is the <dialog>'s (see the Modal's onCancel below: it backs out of
 * reject mode first, then closes). Arrows walk the queue,
 * +/-/0 drive the zoom. Decisions stay click-only — no keystroke approves a
 * photo.
 */
function useReviewShortcuts({
  open,
  zoom,
  onPrev,
  onNext,
  applyZoom,
  resetView,
}: {
  open: boolean;
  zoom: number;
  onPrev: () => void;
  onNext: () => void;
  applyZoom: (next: number) => void;
  resetView: () => void;
}) {
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKey = (e: KeyboardEvent) => {
      const typing = isTypingTarget(e.target);

      if (typing) {
        return;
      } else if (e.key === 'ArrowLeft') {
        onPrev();
      } else if (e.key === 'ArrowRight') {
        onNext();
      } else if (e.key === '+' || e.key === '=') {
        applyZoom(zoom * ZOOM_STEP);
      } else if (e.key === '-') {
        applyZoom(zoom / ZOOM_STEP);
      } else if (e.key === '0') {
        resetView();
      } else {
        return;
      }
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [open, zoom, onPrev, onNext, applyZoom, resetView]);
}

/**
 * The one place a pending photo is approved or rejected. The queue itself
 * carries no decision controls: a moderator opens this dialog, sees the photo
 * large enough to actually judge it, and decides here. After a decision the
 * parent keeps the dialog open on the next photo in the queue.
 */
export function PhotoReviewDialog({
  item,
  index,
  total,
  canApprove,
  busy,
  error,
  onApprove,
  onReject,
  onPrev,
  onNext,
  onClose,
}: PhotoReviewDialogProps) {
  const { t } = useTranslation();
  // Rejecting starts from a filled-in reason — the common case by far, and one
  // less thing to type before moving on. It stays fully editable, and clearing
  // it sends no reason at all.
  const defaultReason = t('admin.moderation.review.defaultReason');
  const view = usePhotoView();
  const { zoom, applyZoom, resetView } = view;
  const [rejecting, setRejecting] = useState(false);
  const form = useRejectReasonForm(defaultReason, onReject);
  const imgRef = useRef<HTMLImageElement>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);

  const open = item !== null;

  // Every new photo starts un-zoomed and out of reject mode (set-during-render,
  // not an effect — the first frame of each photo is already clean). Leaving
  // reject mode is what drops a reason typed for the previous photo: entering
  // it again resets the field (onStartReject). Resetting the form here instead
  // would update its <form.Field> subscribers during this render.
  const [shownId, setShownId] = useState(item?.id ?? null);
  if ((item?.id ?? null) !== shownId) {
    setShownId(item?.id ?? null);
    resetView();
    setRejecting(false);
  }

  const cancelReject = useCallback(() => {
    setRejecting(false);
  }, []);

  useEffect(() => {
    // Select rather than merely focus: the field arrives pre-filled, so a
    // moderator who wants their own wording types straight over the default.
    if (rejecting) {
      reasonRef.current?.select();
    }
  }, [rejecting]);

  useReviewShortcuts({
    open,
    zoom,
    onPrev,
    onNext,
    applyZoom,
    resetView,
  });

  // Wheel-to-zoom needs a non-passive listener to cancel the page scroll,
  // which React's synthetic onWheel does not give us.
  useEffect(() => {
    const el = imgRef.current;
    if (!open || !el) {
      return undefined;
    }
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      applyZoom(zoom * (e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel);
    };
  }, [open, zoom, applyZoom]);

  if (!item) {
    return null;
  }

  const subject = item.userName ?? item.userUpn ?? item.userOid;

  // A native <dialog> (Modal): top layer, so the `transform` that `main > *`
  // keeps from its rise animation no longer captures this overlay; the page
  // behind is inert and does not scroll. Escape backs out of reject mode
  // first, then closes; a click beside the panel closes.
  return (
    <Modal
      aria-label={t('admin.moderation.review.title')}
      onClose={onClose}
      onCancel={rejecting ? cancelReject : onClose}
      className="backdrop:bg-ink/75"
    >
      <div className="flex h-[92vh] w-full max-w-[920px] flex-col border border-ink bg-card shadow-[6px_6px_0_var(--ink)]">
        <ReviewHeader
          item={item}
          subject={subject}
          index={index}
          total={total}
          onClose={onClose}
        />
        <ReviewNotice />
        <PhotoViewport
          imgRef={imgRef}
          src={item.thumbnailUrl}
          alt={subject}
          view={view}
        />
        <SubmissionMeta item={item} />

        {/* Decision area */}
        <div className="border-t border-t-ink px-5 py-4">
          {error && (
            <div className="mb-3">
              <ErrorMessage message={error} size="xs" />
            </div>
          )}
          {rejecting ? (
            <RejectReasonFields
              form={form}
              reasonRef={reasonRef}
              busy={busy}
              onCancel={cancelReject}
            />
          ) : (
            <DecisionButtons
              total={total}
              canApprove={canApprove}
              busy={busy}
              onPrev={onPrev}
              onNext={onNext}
              onApprove={onApprove}
              onStartReject={() => {
                // Back to the default each time, so a reason abandoned on a
                // previous attempt never resurfaces.
                form.setFieldValue('reason', defaultReason);
                setRejecting(true);
              }}
            />
          )}
        </div>
      </div>
    </Modal>
  );
}

/** Header — who, and where we are in the queue. */
function ReviewHeader({
  item,
  subject,
  index,
  total,
  onClose,
}: {
  item: PendingQueueRow;
  subject: string;
  index: number;
  total: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3 border-b border-b-rule px-5 py-3">
      <div className="min-w-0 flex-1">
        <p className="display truncate text-base text-ink">{subject}</p>
        {item.userUpn && item.userName && (
          <p className="mono truncate text-xs text-muted">{item.userUpn}</p>
        )}
      </div>
      <span className="epi-tag ink">
        {t('admin.moderation.review.position', {
          index: index + 1,
          total,
        })}
      </span>
      <button
        type="button"
        onClick={onClose}
        className="btn-ghost"
        style={zoomButtonStyle}
        aria-label={t('admin.moderation.review.close')}
      >
        ✕
      </button>
    </div>
  );
}

/** Standing reminder of what a valid photo is — the whole point of routing
 *  every decision through this dialog. */
function ReviewNotice() {
  const { t } = useTranslation();
  return (
    <div className="border-b border-b-rule bg-[rgba(255,193,7,0.12)] px-5 py-3 text-xs text-ink">
      <strong>{t('admin.moderation.review.noticeTitle')}</strong>{' '}
      {t('admin.moderation.review.notice')}
    </div>
  );
}

/** Photo viewport — zoom and pan happen inside these bounds. */
function PhotoViewport({
  imgRef,
  src,
  alt,
  view,
}: {
  imgRef: React.RefObject<HTMLImageElement | null>;
  src: string;
  alt: string;
  view: PhotoView;
}) {
  const { t } = useTranslation();
  const { zoom, offset, applyZoom, resetView } = view;
  return (
    <div className="relative flex min-h-[160px] flex-1 items-center justify-center overflow-hidden bg-bg">
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        draggable={false}
        /* Absolute + inset rather than max-height: the panel sizes to its
           content, so a percentage height on the photo has nothing
           definite to resolve against and the photo overflows. */
        className="absolute inset-3 size-[calc(100%-1.5rem)] touch-none object-contain select-none"
        style={{
          transform: `translate(${String(offset.x)}px, ${String(offset.y)}px) scale(${String(zoom)})`,
          cursor: zoom > MIN_ZOOM ? 'grab' : 'zoom-in',
        }}
        onDoubleClick={() => {
          applyZoom(zoom > MIN_ZOOM ? MIN_ZOOM : 2);
        }}
        onPointerDown={view.startDrag}
        onPointerMove={view.moveDrag}
        onPointerUp={view.endDrag}
        onPointerCancel={view.endDrag}
      />

      {/* Zoom controls, floated over the viewport */}
      <div className="absolute top-3 right-3 flex items-center gap-2">
        <span className="epi-tag ink" aria-live="polite">
          {Math.round(zoom * 100)}%
        </span>
        <button
          type="button"
          onClick={() => {
            applyZoom(zoom / ZOOM_STEP);
          }}
          disabled={zoom <= MIN_ZOOM}
          className="btn-ghost bg-card disabled:opacity-40"
          style={{ ...zoomButtonStyle }}
          aria-label={t('admin.moderation.review.zoomOut')}
        >
          −
        </button>
        <button
          type="button"
          onClick={() => {
            applyZoom(zoom * ZOOM_STEP);
          }}
          disabled={zoom >= MAX_ZOOM}
          className="btn-ghost bg-card disabled:opacity-40"
          style={{ ...zoomButtonStyle }}
          aria-label={t('admin.moderation.review.zoomIn')}
        >
          +
        </button>
        <button
          type="button"
          onClick={resetView}
          disabled={zoom === MIN_ZOOM && offset.x === 0 && offset.y === 0}
          className="btn-ghost bg-card disabled:opacity-40"
          style={{ ...zoomButtonStyle }}
        >
          {t('admin.moderation.review.reset')}
        </button>
      </div>
    </div>
  );
}

/** Submission metadata. */
function SubmissionMeta({ item }: { item: PendingQueueRow }) {
  const { t } = useTranslation();
  const submittedBySomeoneElse =
    item.submittedByOid && item.submittedByOid !== item.userOid;
  return (
    <div className="flex flex-wrap gap-x-6 gap-y-1 border-t border-t-rule px-5 py-2">
      <p className="label">
        {t('admin.moderation.submittedAt')}{' '}
        {new Date(item.submittedAt).toLocaleString()}
      </p>
      {submittedBySomeoneElse && (
        <p className="label">
          {t('admin.moderation.submittedBy')}{' '}
          {item.submittedByName ?? item.submittedByUpn ?? item.submittedByOid}
        </p>
      )}
      <p className="label">{t('admin.moderation.review.hint')}</p>
    </div>
  );
}

function RejectReasonFields({
  form,
  reasonRef,
  busy,
  onCancel,
}: {
  form: RejectReasonForm;
  reasonRef: React.RefObject<HTMLTextAreaElement | null>;
  busy: boolean;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
    >
      <label className="label mb-1 block" htmlFor={REASON_FIELD_ID}>
        {t('admin.moderation.rejectPrompt')}
      </label>
      <form.Field name="reason">
        {(field) => (
          <textarea
            id={REASON_FIELD_ID}
            ref={reasonRef}
            value={field.state.value}
            onChange={(e) => field.handleChange(e.target.value)}
            onBlur={field.handleBlur}
            maxLength={500}
            rows={2}
            disabled={busy}
            className="w-full border border-ink bg-card px-3 py-2 text-sm text-ink"
            placeholder={t('admin.moderation.rejectReasonPlaceholder')}
          />
        )}
      </form.Field>
      <div className="mt-3 flex items-center gap-3">
        {/* Subscribed on its own, so a keystroke re-renders the counter
            and not the dialog around it. */}
        <form.Subscribe selector={(state) => state.values.reason.length}>
          {(length) => <span className="label text-muted">{length} / 500</span>}
        </form.Subscribe>
        <div className="flex-1" />
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="btn-link text-[11px] disabled:opacity-50"
        >
          {t('admin.moderation.cancel')}
        </button>
        <button
          type="submit"
          disabled={busy}
          className="btn-ghost border-coral px-[28px] py-[14px] text-[13px] text-coral disabled:opacity-50"
        >
          {t('admin.moderation.confirmReject')}
        </button>
      </div>
    </form>
  );
}

function DecisionButtons({
  total,
  canApprove,
  busy,
  onPrev,
  onNext,
  onApprove,
  onStartReject,
}: {
  total: number;
  canApprove: boolean;
  busy: boolean;
  onPrev: () => void;
  onNext: () => void;
  onApprove: () => void;
  onStartReject: () => void;
}) {
  const { t } = useTranslation();
  const navDisabled = busy || total < 2;
  return (
    <div className="flex flex-wrap items-center gap-3">
      <button
        type="button"
        onClick={onPrev}
        disabled={navDisabled}
        className="btn-link text-[11px] disabled:opacity-40"
      >
        ← {t('admin.moderation.review.prev')}
      </button>
      <button
        type="button"
        onClick={onNext}
        disabled={navDisabled}
        className="btn-link text-[11px] disabled:opacity-40"
      >
        {t('admin.moderation.review.next')} →
      </button>
      <div className="flex-1" />
      <button
        type="button"
        onClick={onStartReject}
        disabled={busy}
        className="btn-ghost border-coral px-[28px] py-[14px] text-[13px] text-coral disabled:opacity-50"
      >
        {t('admin.moderation.reject')}
      </button>
      <button
        type="button"
        onClick={onApprove}
        disabled={busy || !canApprove}
        title={canApprove ? undefined : t('photo.pending.fourEyes')}
        className="btn-primary disabled:opacity-50"
      >
        {t('admin.moderation.approve')}
      </button>
    </div>
  );
}
